/**
 * One trial: the output, then its checks. Code checks are short, so they share one line
 * as chips; the reason of one that did not pass is under them. Judge checks each get a
 * line, as their details are long.
 */
import { ChevronDown, ChevronRight, CircleCheck, CircleQuestionMark, CircleX, Wrench } from 'lucide-react';
import { useState } from 'react';
import type { CheckResult, EvalSuggestion, StoredTrialResult } from '../../types/evalTypes';
import type { CostSplit } from '../../utils/evalCost';
import { CostSplitText } from '../CostSplitText';
import { formatDuration } from '../../utils/evalFormat';
import { JudgeFlagControl } from './JudgeFlagControl';
import { SuggestionCard } from './SuggestionCard';

const VerdictIcon = ({ verdict, size = 14 }: Pick<CheckResult, 'verdict'> & { size?: number }) => {
  const nudge = size === 14 ? 'mt-0.5' : '';
  if (verdict === 'pass') return <CircleCheck size={size} className={`text-green-600 shrink-0 ${nudge}`} aria-label="pass" />;
  if (verdict === 'fail') return <CircleX size={size} className={`text-red-600 shrink-0 ${nudge}`} aria-label="fail" />;
  return <CircleQuestionMark size={size} className={`text-amber-600 shrink-0 ${nudge}`} aria-label="unknown" />;
};

/** Dark text on every chip; the icon and the border carry the verdict. */
const chipTone: Record<CheckResult['verdict'], string> = {
  pass: 'border-gray-300 bg-white text-gray-800',
  fail: 'border-red-300 bg-red-50 text-red-900 font-semibold',
  unknown: 'border-amber-300 bg-amber-50 text-amber-900 font-semibold',
};

const groupLabel = 'text-[11px] font-semibold uppercase tracking-wide text-gray-600';

const CodeChecks = ({ checks }: { checks: CheckResult[] }) => {
  const notPassed = checks.filter((check) => check.verdict !== 'pass' && check.detail);
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        <span className={`${groupLabel} mr-1`}>Code</span>
        {checks.map((check) => (
          <span
            key={check.id}
            title={check.detail}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-mono ${chipTone[check.verdict]}`}
          >
            <VerdictIcon verdict={check.verdict} size={12} />
            {check.id}
          </span>
        ))}
      </div>
      {notPassed.map((check) => (
        <p key={check.id} className="text-xs text-gray-800">
          <span className="font-mono">{check.id}</span>: {check.detail}
        </p>
      ))}
    </div>
  );
};

/** "3 interpretations" when every line is one, else "details". */
const detailSummary = (detail: string): string => {
  const lines = detail.split('\n').filter((line) => line.trim() !== '');
  return lines.every((line) => line.startsWith('Interpretation')) ? `${lines.length} interpretation${lines.length === 1 ? '' : 's'}` : 'details';
};

/** A check that passed keeps its notes closed, as they do not change the verdict; the others show why. */
const JudgeCheckRow = ({ check }: { check: CheckResult }) => {
  const [open, setOpen] = useState(check.verdict !== 'pass');
  return (
    <li className="flex items-start gap-1.5 text-xs">
      <VerdictIcon verdict={check.verdict} />
      <span className="min-w-0">
        <span className="font-mono text-gray-900">{check.id}</span>
        {check.detail && check.verdict === 'pass' && (
          <button type="button" onClick={() => setOpen(!open)} className="ml-2 inline-flex items-center text-gray-600 hover:text-gray-900">
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {detailSummary(check.detail)}
          </button>
        )}
        {check.detail && open && <span className="block whitespace-pre-wrap text-gray-800 mt-0.5">{check.detail}</span>}
      </span>
    </li>
  );
};

const JudgeChecks = ({ checks }: { checks: CheckResult[] }) => (
  <div className="space-y-1">
    <span className={`block ${groupLabel}`}>Judge</span>
    <ul className="space-y-1">
      {checks.map((check) => (
        <JudgeCheckRow key={check.id} check={check} />
      ))}
    </ul>
  </div>
);

const statusBadge = (result: StoredTrialResult) => {
  if (result.status === 'error') return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">Error</span>;
  if (result.status === 'skipped') return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Skipped</span>;
  return result.passed ? (
    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Passed</span>
  ) : (
    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">Failed</span>
  );
};

interface TrialCardProps {
  result: StoredTrialResult;
  /** Estimated cost of the model call and of its judge call, apart. */
  cost: CostSplit;
  currentUid: string | undefined;
  onSaveFlag: (checkIds: string[], note: string) => Promise<void>;
  /** Pending judge-flag suggestions from Claude for this trial. */
  suggestions: EvalSuggestion[];
  onDecideSuggestion: (suggestionId: string, accept: boolean, rejectReason?: string) => Promise<void>;
}

export const TrialCard = ({ result, cost, currentUid, onSaveFlag, suggestions, onDecideSuggestion }: TrialCardProps) => {
  const { output } = result;
  const stopReason = output?.meta?.stopReason;
  const codeChecks = result.checks.filter((check) => check.kind === 'code');
  const judgeChecks = result.checks.filter((check) => check.kind === 'judge');

  return (
    <div className="p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700">Trial {result.trial}</span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {result.status !== 'skipped' && <CostSplitText cost={cost} />}
          {output && <span className="text-xs text-gray-600">{formatDuration(output.durationMs)}</span>}
          {statusBadge(result)}
        </div>
      </div>

      {result.error && <p className="text-sm text-red-700 font-mono break-words">{result.error}</p>}

      {output && (
        <>
          {stopReason && stopReason !== 'end_turn' && (
            <p className="text-xs text-red-700">
              stop_reason: <span className="font-mono">{stopReason}</span>
            </p>
          )}
          <p className="text-sm whitespace-pre-wrap text-gray-900 bg-gray-50 rounded p-2">{output.text || <em className="text-gray-400">(no text)</em>}</p>
          {output.toolCalls.length > 0 && (
            <ul className="space-y-1">
              {output.toolCalls.map((call) => (
                <li key={call.id} className="flex flex-wrap items-baseline gap-1.5 text-xs">
                  <Wrench size={12} className="text-gray-500 shrink-0 self-center" aria-hidden />
                  <span className="font-mono font-semibold text-gray-900">{call.name}</span>
                  {call.arguments && call.arguments !== '{}' && <span className="font-mono text-gray-700 break-all">{call.arguments}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {codeChecks.length > 0 && <CodeChecks checks={codeChecks} />}
      {judgeChecks.length > 0 && <JudgeChecks checks={judgeChecks} />}

      {suggestions.map((suggestion) => (
        <SuggestionCard
          key={suggestion.suggestionId}
          suggestion={suggestion}
          currentUid={currentUid}
          onDecide={(accept, rejectReason) => onDecideSuggestion(suggestion.suggestionId, accept, rejectReason)}
        />
      ))}

      <JudgeFlagControl result={result} currentUid={currentUid} onSave={onSaveFlag} />
    </div>
  );
};
